import { Injectable } from '@angular/core';
import { BazisSrvService } from '@bazis/shared/services/srv.service';
import { BazisAuthService } from '@bazis/shared/services/auth.service';
import { TranslocoService } from '@jsverse/transloco';
import { from, Observable, switchMap, combineLatest, of, concatMap, toArray } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class BazisNCALayerService {
    SOCKET_URL = 'wss://127.0.0.1:13579/';

    webSocket: WebSocket;

    constructor(
        protected authService: BazisAuthService,
        protected srvService: BazisSrvService,
        protected translocoService: TranslocoService,
    ) {}

    signDocuments$(itemsToSign: string[]): Observable<any> {
        return from(this.getWsConnect()).pipe(
            switchMap((webSocket) => this.signArrayOfDocuments$(webSocket, itemsToSign)),
            catchError((e) => {
                console.log(e);
                return of(null);
            }),
        );
    }

    signArrayOfDocuments$(webSocket: WebSocket, itemsToSign) {
        return from(itemsToSign).pipe(
            concatMap((item) => from(this.request(webSocket, item))),
            toArray(),
        );
    }

    getWsConnect(): Promise<WebSocket> {
        if (this.webSocket && this.webSocket.readyState < 2) {
            console.log(
                'reusing the socket connection [state = ' +
                    this.webSocket.readyState +
                    ']: ' +
                    this.webSocket.url,
            );
            return Promise.resolve(this.webSocket);
        }

        return new Promise((resolve, reject) => {
            this.webSocket = new WebSocket(this.SOCKET_URL);

            this.webSocket.onopen = () => {
                console.log(
                    'socket connection is opened [state = ' +
                        this.webSocket.readyState +
                        ']: ' +
                        this.webSocket.url,
                );
                resolve(this.webSocket);
            };

            this.webSocket.onerror = (err) => {
                console.error('socket connection error : ', err);
                reject(err);
            };

            this.webSocket.onclose = (event) => {
                if (event.wasClean) {
                    console.error('socket connection is closed ');
                } else {
                    console.log('Connection error');
                    this.openDialog();
                }
                console.log('Code: ' + event.code + ' Reason: ' + event.reason);
            };
        });
    }

    request(webSocket, dataToSign) {
        let signInfo = {
            module: 'kz.gov.pki.knca.basics',
            method: 'sign',
            args: {
                // allowedStorages: []
                format: 'cms',
                data: dataToSign,
                signingParams: {
                    decode: true, // false по умолчанию. Если true, то data будет декодировано из base64
                    encapsulate: false, // true по умолчанию, встраивает значение в cms. Если false, то будет detached
                    digested: false, // false по умолчанию. Если true, то входящие данные уже захешированы.,
                    tsaProfile: {}, // Пока пустой объект. Если указан, то будет добавлена метка времени сервиса tsp.pki.gov.kz.
                },
                signerParams: {
                    extKeyUsageOids: [],
                    iin: '', // ИИН - влияет на список подписей для выбора
                    bin: '', // БИН - влияет на список подписей для выбора
                    serialNumber: '', // Серийный номер сертификата
                    chain: [], // CertPathBuilder will not be invoked. Any certificate will be accepted. массив со списком сертификатов в PEM для построения цепочки. По умолчанию не нужно указывать, тогда будут активны только действующие сертификаты НУЦ РК. При указании пустого массива построение цепочки будет необязательно.
                    // allowExpired: true, // for dev only
                    // allowRevoked: true, // for dev only
                },
                locale: 'ru', // alternative - kk
            },
        };

        let response: any;

        webSocket.send(JSON.stringify(signInfo));

        return new Promise((resolve, reject) => {
            webSocket.onmessage = ({ data }) => {
                response = JSON.parse(data);
                if (!response || response.status === undefined) return;

                if (response.status === true) {
                    if (response.body?.result?.length > 0) {
                        resolve(btoa(response.body.result[0]));
                    } else {
                        reject(null);
                    }
                } else {
                    reject(response.code);
                    alert(response.code);
                }
            };
        });
    }

    openDialog() {
        if (
            confirm('Ошибка при подключении к NCALayer. Запустите NCALayer и нажмите ОК') === true
        ) {
            location.reload();
        }
    }
}
